import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, Settings, UserCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const UserMenu = ({
    userEmail = "user@example.com",
    userImage = null,
    handleSignOut = () => console.log("Sign out clicked"),
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Get initials from display name if available, otherwise from email
    const initials = user?.user_metadata?.full_name
        ? user.user_metadata.full_name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
        : userEmail
            .split('@')[0]
            .split('.')
            .map(part => part[0])
            .join('')
            .toUpperCase();

    // Get display name for the menu
    const displayName = user?.user_metadata?.full_name;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="h-8 pl-2 pr-3 relative flex items-center gap-2 hover:bg-accent dark:hover:bg-accent focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 focus:ring-offset-0"
                >
                    <Avatar className="h-6 w-6">
                        {userImage && <AvatarImage src={userImage} alt="User avatar" />}
                        <AvatarFallback className="bg-primary/10 dark:bg-primary/20 text-xs text-foreground dark:text-foreground">
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium text-foreground dark:text-foreground">
                        {displayName || userEmail}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground dark:text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-56"
                align="end"
            >
                <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none text-foreground dark:text-foreground">
                            {displayName || 'My Account'}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground dark:text-muted-foreground">
                            {userEmail}
                        </p>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/profile')}
                >
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/settings')}
                >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 focus:text-red-600 dark:focus:text-red-400 cursor-pointer"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default UserMenu;